import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { getCategoryPath } from '../../services/firestoreService';

/**
 * Breadcrumb Navigation Component
 * Shows hierarchical path: Home > Parent > Current
 *
 * @param {string} categoryId - Current category ID
 * @param {boolean} showHome - Show home link (default: true)
 * @param {string} homeLabel - Label for home link (default: "Home")
 * @param {string} homeLink - URL for home link (default: "/main")
 */
const Breadcrumb = ({
  categoryId,
  showHome = true,
  homeLabel = "Home",
  homeLink = "/main"
}) => {
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPath = async () => {
      if (!categoryId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await getCategoryPath(categoryId);

        if (result.success) {
          setPath(result.data);
        } else {
          setError(result.error);
        }
      } catch (err) {
        console.error('Error fetching breadcrumb path:', err);
        setError('Failed to load navigation');
      } finally {
        setLoading(false);
      }
    };

    fetchPath();
  }, [categoryId]);

  if (!categoryId) return null;
  if (loading) {
    return (
      <nav aria-label="Breadcrumb" className="mb-4">
        <div className="flex items-center gap-2 text-sm">
          <div className="h-4 w-12 bg-gray-200 rounded animate-pulse"></div>
          <ChevronRight className="w-4 h-4 text-gray-300" />
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </nav>
    );
  }

  if (error || !path.length) return null;

  // Get current category (last in path)
  const currentCategory = path[path.length - 1];

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex items-center flex-wrap gap-2 text-sm">
        {/* Home Link */}
        {showHome && (
          <li className="flex items-center gap-2">
            <Link
              to={homeLink}
              className="flex items-center gap-1.5 text-gray-600 hover:text-blue-600 transition-colors font-medium"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">{homeLabel}</span>
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </li>
        )}

        {/* Category Path (all except last) */}
        {path.slice(0, -1).map((category) => (
          <li key={category.id} className="flex items-center gap-2">
            <Link
              to={`/category/${category.slug || category.id}`}
              className="text-gray-600 hover:text-blue-600 transition-colors font-medium truncate max-w-[150px]"
              title={category.name}
            >
              {category.name}
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </li>
        ))}

        {/* Current Category (not a link) */}
        <li
          className="text-gray-900 font-semibold truncate max-w-[200px]"
          aria-current="page"
          title={currentCategory.name}
        >
          {currentCategory.name}
        </li>
      </ol>
    </nav>
  );
};

/**
 * Mobile Breadcrumb (Compact Version)
 * Shows: ← Parent | Current
 * Used for mobile screens < 768px
 */
export const MobileBreadcrumb = ({ categoryId }) => {
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPath = async () => {
      if (!categoryId) {
        setLoading(false);
        return;
      }

      try {
        const result = await getCategoryPath(categoryId);
        if (result.success) {
          setPath(result.data);
        }
      } catch (err) {
        console.error('Error fetching mobile breadcrumb:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPath();
  }, [categoryId]);

  if (loading || !path.length) return null;

  const currentCategory = path[path.length - 1];
  const parentCategory = path.length > 1 ? path[path.length - 2] : null;

  return (
    <div className="flex items-center gap-3 mb-4 md:hidden">
      {parentCategory && (
        <Link
          to={`/category/${parentCategory.slug || parentCategory.id}`}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">{parentCategory.name}</span>
        </Link>
      )}
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">Current</span>
        <span className="text-sm font-semibold text-gray-900">{currentCategory.name}</span>
      </div>
    </div>
  );
};

export default Breadcrumb;
